import { Ref, forwardRef } from "react"
import { Formik, FormikHelpers, FormikProps } from "formik"
import * as yup from "yup"
import { StandardFormikErrorScrollWrapper } from "../../../../../shared/components/forms/StandardFormikErrorScrollWrapper"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { toCurrency, toDineroLossy, toMonetaryAmount } from "../../../../currency/currencyLib"
import { CreateUpdateProductFormContent } from "./CreateUpdateProductFormContent"
import { CreateProduct, CreateUpdateProductFormValues } from "../productsTypes"

type CreateUpdateProductFormProps = Readonly<{
    initialValues?: CreateUpdateProductFormValues | null
    onSubmit: (formData: CreateProduct) => void
}>
type FormikRef = Ref<FormikProps<CreateUpdateProductFormValues>>

export const CreateUpdateProductForm = forwardRef(
    ({ initialValues, onSubmit }: CreateUpdateProductFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const defaultInitialValues = {
            productGroup: null,
            productNumber: "",
            name: "",
            price: undefined,
            currency: "DKK",
            shops: [],
            // priceLists: [],
            priceList: null,
            description: "",
            isActive: true,
            availabilityDetails: {
                allowPreOrder: false,
            },
            paymentDetails: {
                isPriceHidden: false,
            },
            productGroupCode: undefined,
            itemLimits: null,
            stock: null,
        }

        const validationSchema = yup.object({
            productGroup: yup
                .object()
                .shape({
                    id: yup.number(),
                    name: yup.string(),
                    isActive: yup.boolean(),
                })
                .required(l10n.getString("required-field")),
            productNumber: yup.string(),
            name: yup.string().required(l10n.getString("required-field")),
            price: yup.number().required(l10n.getString("required-field")),
            currency: yup.string(),
            shops: yup.array().of(
                yup.object().shape({
                    id: yup.number(),
                    name: yup.string(),
                    isActive: yup.boolean(),
                }),
            ),
            // priceLists: yup
            //     .array()
            //     .of(
            //         yup.object().shape({
            //             id: yup.number(),
            //             name: yup.string(),
            //             isActive: yup.boolean(),
            //         }),
            //     )
            //     .min(1, l10n.getString("required-field")),
            priceList: yup
                .object()
                .shape({
                    id: yup.number(),
                })
                .nullable()
                .required(l10n.getString("required-field")),
            //add characters limit not to let ppl send very big texts to server, maybe add that character limit to form field like we did in gopay app
            description: yup.string(),
            isActive: yup.boolean(),
            itemLimits: yup
                .object({
                    maxDailyItemsPerPerson: yup
                        .number()
                        .nullable()
                        .transform((value) => (isNaN(value) ? undefined : value))
                        .min(1, l10n.getString("product-create-item-limits-max-error"))
                        .optional(),
                    minItemsPerOrder: yup
                        .number()
                        .nullable()
                        .transform((value) => (isNaN(value) ? undefined : value))
                        .min(2, l10n.getString("product-create-item-limits-min-error"))
                        .optional(),
                })
                .nullable()
                .test("either-or", l10n.getString("product-create-item-limits-error"), function (value) {
                    if (value === null || value === undefined) return true
                    return !!value.maxDailyItemsPerPerson || !!value.minItemsPerOrder
                }),
            stock: yup
                .object({
                    total: yup
                        .number()
                        .nullable()
                        .transform((value) => (isNaN(value) ? undefined : value))
                        .min(0)
                        .optional(),
                    itemsForSalePerDay: yup
                        .number()
                        .nullable()
                        .transform((value) => (isNaN(value) ? undefined : value))
                        .min(0)
                        .optional(),
                })
                .nullable(),
        })

        function handleSubmit(
            values: CreateUpdateProductFormValues,
            _helpers: FormikHelpers<CreateUpdateProductFormValues>,
        ) {
            const {
                productGroup,
                shops,
                price,
                productPhoto,
                productDescription,
                availabilityDetails,
                deadline,
                // priceLists,
                priceList,
                deliveryDetails,
                itemLimits,
                productGroupCode,
                stock,
                ...productValues
            } = values

            const productGroupCodeBody = productGroupCode
                ? {
                      productGroupCode: {
                          id: productGroupCode.id,
                      },
                  }
                : {}

            const modifyData = {
                ...productValues,
                ...productGroupCodeBody,
                price: toMonetaryAmount(toDineroLossy(values.price!, toCurrency(values.currency))),
                productGroup: {
                    id: productGroup?.id!,
                },
                shops: shops.map((shop) => ({
                    id: shop.id,
                })),
                ...(productDescription
                    ? {
                          productDescription: {
                              ...productDescription,
                              allergens: (productDescription.allergens ?? []).map((allergen) => ({
                                  id: allergen.id,
                              })),
                          },
                      }
                    : {}),
                availabilityDetails: {
                    allowPreOrder: !availabilityDetails?.allowPreOrder,
                    ...(availabilityDetails?.dateInterval
                        ? {
                              dateInterval: {
                                  ...(availabilityDetails?.dateInterval?.from
                                      ? { from: availabilityDetails?.dateInterval?.from.toISODate() }
                                      : {}),
                                  ...(availabilityDetails?.dateInterval?.to
                                      ? { to: availabilityDetails?.dateInterval?.to.toISODate() }
                                      : {}),
                              },
                          }
                        : {}),
                },
                ...(deadline
                    ? {
                          deadline: {
                              purchases: {
                                  ...deadline.purchases,
                                  timeOfDay: deadline.purchases.timeOfDay.toFormat("HH:mm"),
                              },
                              cancellation: {
                                  ...deadline.cancellation,
                                  timeOfDay: deadline.cancellation.timeOfDay.toFormat("HH:mm"),
                              },
                          },
                      }
                    : {}),
                // ...(priceLists && priceLists.length > 0
                //     ? {
                //           priceLists: priceLists.map((priceList) => ({
                //               id: priceList.id,
                //           })),
                //       }
                //     : {}),
                ...(priceList
                    ? {
                          priceLists: [{ id: priceList.id }],
                      }
                    : {}),
                ...(deliveryDetails
                    ? {
                          deliveryDetails: { location: { id: deliveryDetails?.location.id } },
                      }
                    : {}),
                ...(itemLimits && (itemLimits.maxDailyItemsPerPerson || itemLimits.minItemsPerOrder)
                    ? {
                          itemLimits: itemLimits,
                      }
                    : {}),
                ...(stock && (stock.inventory || stock.itemsForSalePerDay != null)
                    ? {
                          stock: {
                              ...(stock.inventory ? { inventory: stock.inventory } : {}),
                              ...(stock.itemsForSalePerDay != null
                                  ? { sales: { daily: { itemsForSale: stock.itemsForSalePerDay } } }
                                  : {}),
                          },
                      }
                    : {}),
            }

            if (!productPhoto?.uid && productPhoto?.url) {
                onSubmit({
                    ...modifyData,
                    productPhoto: {
                        url: productPhoto?.url,
                    },
                })
            } else {
                onSubmit({
                    ...modifyData,
                    ...(productPhoto?.uid !== undefined
                        ? {
                              productPhoto: {
                                  uid: productPhoto?.uid,
                              },
                          }
                        : {}),
                })
            }
        }

        return (
            <>
                <Formik<CreateUpdateProductFormValues>
                    enableReinitialize
                    initialValues={initialValues ? initialValues : defaultInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                    validateOnBlur={false}
                    validateOnChange={false}
                >
                    <StandardFormikErrorScrollWrapper<CreateUpdateProductFormValues>
                        children={<CreateUpdateProductFormContent isEditMode={!!initialValues} />}
                    />
                </Formik>
            </>
        )
    },
)
